import {setInShallowClone} from '../../../util/utility';
import * as PanelTypes from '../panel/types';
import * as PanelBankSectionConfigTypes from '../panelBankSectionConfig/types';
import {ActionType, ViewReducerState} from '../reducerSupport';
import * as Actions from './actions';
import {arrayMove} from './arrayMove';
import * as Types from './types';

/**
 * This actions handles moving the active panel to the target section's position in the array.
 * This reorders the panelRefs in the panelBankSectionConfig. This assumes you're moving the
 * panel WITHIN THE SAME SECTION. Please use movePanelToDiffSection for moving a panel to a different section.
 *
 * @param ref This is the panel bank config ref
 * @param active This is the panel that is being moved
 * @param target This is the info that indicates what position the active panel will be moved to and which section
 */
export const movePanelAction = (
  state: ViewReducerState,
  ref: Types.Ref,
  active: {
    panelRef: PanelTypes.Ref;
    sectionRef: PanelBankSectionConfigTypes.Ref;
  },
  target: {
    panelRef: PanelTypes.Ref;
    sectionRef: PanelBankSectionConfigTypes.Ref;
  }
): [ViewReducerState, ActionType] => {
  const activeSection =
    state.parts[active.sectionRef.type][active.sectionRef.id];
  const activePanelIndex = activeSection.panelRefs.findIndex(
    panelRef => panelRef.id === active.panelRef.id
  );

  const targetSection =
    state.parts[target.sectionRef.type][target.sectionRef.id];
  const targetPanelIndex = targetSection.panelRefs.findIndex(
    panelRef => panelRef.id === target.panelRef.id
  );

  // Validate that both panels exist
  if (activePanelIndex === -1 || targetPanelIndex === -1) {
    console.warn('movePanelAction: Panel not found', {
      activePanelRefId: active.panelRef.id,
      targetPanelRefId: target.panelRef.id,
      activeIndex: activePanelIndex,
      targetIndex: targetPanelIndex,
    });
    return [state, Actions.noOp(ref)];
  }

  const isMovingToSameSection = active.sectionRef.id === target.sectionRef.id;
  const isMovingToSamePosition = activePanelIndex === targetPanelIndex;

  // If moving to the same section and position, no-op
  if (isMovingToSameSection && isMovingToSamePosition) {
    return [state, Actions.noOp(ref)];
  }

  // Panel is moving to a different section, and should be handled by a different action
  // This is invalid with this action, so we no-op
  if (!isMovingToSameSection) {
    console.error(
      'movePanelAction: Panel is moving to a different section, and should be handled by a different action'
    );
    return [state, Actions.noOp(ref)];
  }

  const newSectionPanelRefs = arrayMove(
    activeSection.panelRefs,
    activePanelIndex,
    targetPanelIndex
  );
  // For undo, we need the panel that will be at the original active position after the move
  const undoTargetPanelRef = newSectionPanelRefs[activePanelIndex];

  const newState = setInShallowClone(
    state,
    ['parts', active.sectionRef.type, active.sectionRef.id, 'panelRefs'],
    newSectionPanelRefs
  );
  const undoAction = Actions.movePanel2(
    ref,
    {
      panelRef: active.panelRef, // The panel we just moved
      sectionRef: active.sectionRef,
    },
    {
      panelRef: undoTargetPanelRef, // The panel now at the original position
      sectionRef: active.sectionRef,
    }
  );

  return [newState, undoAction];
};
